import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { ContentRendererContext } from 'lane-shared/contexts';
import { VisitorManagementFeatureProperties } from 'application-types';

import { InviteNameField } from '../components';
import { getVisitorManagementFeatures } from '../types/visitorFormInteraction';

export type AddInviteNameBlockProps = {
  value: string | null | undefined;
  onChange: (value: string) => void;
  show: boolean;
  isPreview?: boolean;
  properties: VisitorManagementFeatureProperties;
  submitAttempted?: boolean;
  /** When set, indicates a successful form submission; used to reset isPristine for the next invite. */
  submissionCompletedAt?: Date;
  /** True when visitors were added via bulk/CSV; inviteName is required only then when setting is requiredWhenBulk */
  uploadedViaBulkImport?: boolean;
  /** When true, invite name is required for this interaction (form has block + CSV bulk); from validations. */
  isInviteNameRequired?: boolean;
};

function getInviteNameValidation(
  isRequiredForBulk: boolean,
  requiredMessage: string
): yup.StringSchema<string | undefined> {
  return isRequiredForBulk
    ? yup.string().trim().required(requiredMessage)
    : (yup.string().trim().optional() as yup.StringSchema<string | undefined>);
}

/**
 * Renders the Invite Name block; value is stored in interaction.features.VisitorManagement.inviteName.
 * State is driven only via onInteractionUpdated (from context). The onChange and properties props
 * are accepted for API consistency but unused; the block does not use a local callback.
 */
export function AddInviteNameBlock({
  value,
  onChange: _onChange,
  show,
  isPreview = false,
  properties: _properties,
  submitAttempted = false,
  submissionCompletedAt,
  uploadedViaBulkImport = false,
  isInviteNameRequired,
}: AddInviteNameBlockProps): React.ReactElement | null {
  const { t } = useTranslation();
  const { onInteractionUpdated, interaction, editMode } = useContext(ContentRendererContext);
  const [isPristine, setIsPristine] = useState(true);
  const [localValue, setLocalValue] = useState(() => value ?? '');
  useEffect(() => {
    setLocalValue(value ?? '');
  }, [value]);

  // Ensure payload always includes inviteName when form has this field (even when empty)
  useEffect(() => {
    if (!show || !onInteractionUpdated) return;

    const vmFeatures = getVisitorManagementFeatures(interaction);

    if (vmFeatures && 'inviteName' in vmFeatures) return; // already set

    onInteractionUpdated({
      features: {
        VisitorManagement: { inviteName: value ?? '' },
      },
    } as Parameters<NonNullable<typeof onInteractionUpdated>>[0]);
  }, [show, onInteractionUpdated, interaction, value]);

  useEffect(() => {
    if (submissionCompletedAt) {
      setIsPristine(true);
    }
  }, [submissionCompletedAt]);

  const uploadedViaBulkImportFromContext = Boolean(
    getVisitorManagementFeatures(interaction)?.uploadedViaBulkImport
  );

  const isBulkUpload = uploadedViaBulkImport || uploadedViaBulkImportFromContext;

  const isRequiredForBulk = useMemo(
    () => show && isBulkUpload,
    [show, isBulkUpload]
  );

  const isRequired = isRequiredForBulk && Boolean(isInviteNameRequired);

  const validation = useMemo(() => {
    const requiredMessage = t(
      'web.content.feature.visitorManagement.visitorInvite.form.inviteNameRequiredWhenBulk'
    );

    return getInviteNameValidation(isRequired, requiredMessage);
  }, [isRequired, t]);

  const label = useMemo(
    () =>
      t(
        'web.content.feature.visitorManagement.visitorInvite.form.inviteName'
      ),
    [t]
  );
  const placeholder = useMemo(
    () =>
      t(
        'web.content.feature.visitorManagement.visitorInvite.form.placeholder.inviteName'
      ),
    [t]
  );

  const handleChange = useCallback(
    (newValue: string) => {
      setIsPristine(false);
      setLocalValue(newValue);
      const update = {
        features: {
          VisitorManagement: { inviteName: newValue },
        },
      };

      onInteractionUpdated?.(
        update as Parameters<NonNullable<typeof onInteractionUpdated>>[0]
      );
    },
    [onInteractionUpdated]
  );

  if (!show) return null;

  const showValidationError =
    !isPristine ||
    (submitAttempted && isRequired && !localValue.trim());

  return (
    <InviteNameField
      label={label}
      placeholder={placeholder}
      value={localValue}
      onChange={handleChange}
      validation={validation}
      isPristine={!showValidationError}
      isRequired={isRequired}
      isDisabled={isPreview}
      testId="inviteName"
      editMode={editMode}
    />
  );
}

export default AddInviteNameBlock;
